<template>
    <section class="ai-switch-section" v-if="showAi">
        <Card class="config-item">
            <div class="config-title">
                {{ $t('cloud_ai.feature') }}
                <reo-tip-dialog dialogClass="ai-info-dialog" isBottomWhenMobile>
                    <AiTipContent :showAgreement="false"/>
                </reo-tip-dialog>
            </div>
            <el-switch :value="currentValue"
                @change="onChange"
                active-color="var(--b_6)"
            />
        </Card>
        <reo-dialog v-if="showTipDialog"
            :label-of-confirm="$t('cloud_ai.confirm')"
            @confirm="handleAiFeatureChange(true)"
            @cancel="showTipDialog = false"
            :disableConfirm="!agreement"
        >
            <AiTipContent v-model="agreement"/>
        </reo-dialog>
        <Card class="config-item" v-if="showE2EE">
            <div>
                <div class="config-title">
                    {{ $t('selectProduct.protection') }}
                    <reo-tip-dialog dialogClass="ai-info-dialog" isBottomWhenMobile>
                        <div class="tip-content">
                            <p v-for="text in $t('cloud_ai.e2ee_tip')" :key="String(text)">{{ text }}</p>
                            <reo-link
                                :href="config.e2ee_support"
                                :text="$t('e2ee.learn_more')"
                            />
                        </div>
                    </reo-tip-dialog>
                </div>
            </div>
            <div class="text">
                {{ cmkEnabled? $t('devices.trafficTable.on'): $t('devices.trafficTable.off') }}
            </div>
        </Card>

        <subscription-dialog
            :hasPlanExpired="hasPlanExpired"
            :simManager="simManager"
            :hasCurrentPlan="hasCurrentPlan"
            :showSubscriptionDialog="showSubscriptionDialog"
            @update:showSubscriptionDialog="showSubscriptionDialog = $event"
        >
            <p>{{  $t('devices.trafficTable.subscriptionReminder') }}</p>
        </subscription-dialog>
    </section>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import Card from '@/basic/Card.vue';
import { Switch as ElSwitch } from 'element-ui';
import ReoLink from '@/basic/Link.vue';
import ReoTipDialog from '@/components/ReoTipDialog.vue';
import { config } from '@/config/reolink';
import { mapGetters } from 'vuex';
import { ESeriesCategory, IReolinkDevice, ISubDevice, ISubscription } from '@reolink-cloud/interface';
import { requestWithErrorHandler } from '@/common/js/utils';
import AiTipContent from './components/AiTipContent.vue';
import SubscriptionDialog from '@/view/reolink/user/Subscribe/LockPlan/components/SubscriptionDialog.vue';
import { modifyDeviceAiConfig } from '@/api/ai';
import { getSubscriptions } from '@/api/subscriptions';
import { SimManager } from '@/lib';
import ReoDialog from '@/basic/Dialog.vue';

export default Vue.extend({
    components:{
        Card,
        ElSwitch,
        ReoLink,
        ReoTipDialog,
        AiTipContent,
        SubscriptionDialog,
        ReoDialog
    },
    props:{
        deviceInfo: Object as PropType<IReolinkDevice>,
        showAi: {
            type: Boolean,
            default: false,
        },
        subDevice: Object as PropType<ISubDevice>,
        simManager: Object as PropType<SimManager>,
        hasCurrentPlan:{
            type: Boolean,
            default: true,
        },
    },
    data() {

        return {
            currentValue: false,
            config,
            showTipDialog: false,
            agreement: false,
            showSubscriptionDialog:false,
            currentPlanDetails: undefined as ISubscription | undefined,
        };

    },
    computed:{
        ...mapGetters( ['enforcedCMK'] as const ),
        cmkEnabled(): boolean {

            return !this.currentValue && this.enforcedCMK;

        },
        isLocked(): boolean {

            return !!this.simManager?.data.esimDevice;

        },
        hasPlanExpired(): boolean {

            if ( !this.currentPlanDetails?.endingAt ) {

                return false;

            }

            return this.simManager?.currentSubscription?.effectiveStatus === 'inactive';

        },
        showE2EE(): boolean {

            return this.showAi && this.simManager?.currentSubscription?.planInfo?.series?.category !== ESeriesCategory.CLOUD_DATA_PLAN;

        },
    },
    watch:{
        subDevice: {
            immediate: true,
            handler( subDevice: ISubDevice | undefined ) {

                if ( subDevice?.smartAiEnabled && subDevice.status === 'associated' && subDevice.aiSupport ) {

                    this.currentValue = true;

                } else {

                    this.currentValue = false;

                }

            },
        },
        showSubscriptionDialog( newVal, oldVal ) {

            if ( newVal !== oldVal ) {

                this.getCurrentPlanInfo().catch( error => {

                    console.error( 'Error fetching current plan info:', error );

                });

            }

        }
    },
    methods: {
        async getCurrentPlanInfo() {

            const subscriptions = await getSubscriptions();
            this.currentPlanDetails = subscriptions.items.find( item => item.plan === this.subDevice?.plan ) ?? undefined;

        },
        async handleAiFeatureChange( value: boolean ) {

            await requestWithErrorHandler( async () => modifyDeviceAiConfig(
                {
                    devices: [
                        {
                            uid: this.deviceInfo?.uid ?? '',
                            smartAiEnabled: value,

                        },
                    ],
                }
            ));
            this.currentValue = value;
            this.showTipDialog = false;

        },
        async onChange( value: boolean ): Promise<void> {

            if ( !value ) {

                await this.handleAiFeatureChange( false );
                return;

            }

            if ( this.isLocked ) {

                if ( !this.subDevice?.aiSupport || this.expired ) {

                    this.showSubscriptionDialog = !this.showSubscriptionDialog;
                    return;

                }

                this.showTipDialog = true;
                return;

            }

            this.showTipDialog = true;

        }
    },
});
</script>

<style lang="less" scoped>
.ai-switch-section {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .el-switch {
        margin-top: 8px;
    }
}

.config-item{
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    .config-title{
        font-size: 18px;
        font-weight: 600;
    }
    .text{
        font-size: 18px;
        font-weight: 500;
        color: @font_secondary;
    }
}

</style>

<style lang="less">
.dialog.reo-dialog.ai-info-dialog.bottom .reo-dialog-inner {
    @media @pc {
        --dialog-padding-top: 20px;
        --dialog-padding-right: 20px;
        padding: 48px;
    }
    @media @mobile {
        --dialog-padding-top: 12px;
        --dialog-padding-right: 12px;
        padding: 32px 20px;
    }
}

.tip-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    h4{
        font-size: 20px;
        font-weight: 600;
    }
    ul{
        display: flex;
        flex-direction: column;
        gap: 4px;
        li{
            h5 {
                font-size: 18px;
                font-weight: 600;
            }
        }
    }

}
</style>
